import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { formatDateTime } from '../shared/formatting';

type Company = { id: number; code: string; name: string };
type LockRow = {
    company_id: number;
    fiscal_year: number;
    locked_at: string | null;
    lock_reason: string | null;
    locked_by_name: string | null;
    unlocked_at: string | null;
    unlock_reason: string | null;
    unlocked_by_name: string | null;
    last_snapshot_at: string | null;
};
type Payload = { companies: Company[]; locks: LockRow[] };

type BatchItem = { company_id: number; fiscal_year: number; ok: boolean; output?: string; error?: string };
type BatchResponse = { ok_count: number; failed_count: number; results: BatchItem[] };

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 4, CURRENT_YEAR - 3, CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR];

function cellKey(companyId: number, year: number): string {
    return `${companyId}::${year}`;
}

export function AdminPeriodLocksSection({
    token,
    locale,
    reloadTick,
}: {
    token: string;
    locale: 'hr' | 'en';
    reloadTick: number;
}) {
    const def = adminSectionDefinition('150');
    const title = def ? adminSectionNavLabel(def, locale) : '150';
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [payload, setPayload] = useState<Payload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [lastOutput, setLastOutput] = useState<{ ok: boolean; output: string } | null>(null);
    const [selection, setSelection] = useState<Set<string>>(new Set());
    const [batchBusy, setBatchBusy] = useState<'refresh' | 'lock' | 'unlock' | null>(null);
    const [batchSummary, setBatchSummary] = useState<{ kind: string; ok: number; failed: number; failures: BatchItem[] } | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setPayload(await api<Payload>('/admin/period-locks', token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
            setPayload(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void load(); }, [load, reloadTick]);

    const lockMap = useMemo(() => {
        const m = new Map<string, LockRow>();
        for (const l of payload?.locks ?? []) {
            m.set(cellKey(l.company_id, l.fiscal_year), l);
        }
        return m;
    }, [payload]);

    function toggleCell(companyId: number, year: number) {
        const key = cellKey(companyId, year);
        setSelection((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }

    function selectColumn(year: number) {
        if (!payload) return;
        const allInColumn = payload.companies.map((c) => cellKey(c.id, year));
        const allSelected = allInColumn.every((k) => selection.has(k));
        setSelection((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                allInColumn.forEach((k) => next.delete(k));
            } else {
                allInColumn.forEach((k) => next.add(k));
            }
            return next;
        });
    }

    function selectRow(companyId: number) {
        const allInRow = YEAR_OPTIONS.map((y) => cellKey(companyId, y));
        const allSelected = allInRow.every((k) => selection.has(k));
        setSelection((prev) => {
            const next = new Set(prev);
            if (allSelected) {
                allInRow.forEach((k) => next.delete(k));
            } else {
                allInRow.forEach((k) => next.add(k));
            }
            return next;
        });
    }

    function clearSelection() {
        setSelection(new Set());
    }

    function selectAllUnlocked() {
        if (!payload) return;
        const next = new Set<string>();
        payload.companies.forEach((c) => {
            YEAR_OPTIONS.forEach((y) => {
                const lock = lockMap.get(cellKey(c.id, y));
                if (!lock?.locked_at) {
                    next.add(cellKey(c.id, y));
                }
            });
        });
        setSelection(next);
    }

    function selectAllLocked() {
        if (!payload) return;
        const next = new Set<string>();
        payload.companies.forEach((c) => {
            YEAR_OPTIONS.forEach((y) => {
                const lock = lockMap.get(cellKey(c.id, y));
                if (lock?.locked_at) {
                    next.add(cellKey(c.id, y));
                }
            });
        });
        setSelection(next);
    }

    function selectionItems(): { company_id: number; fiscal_year: number }[] {
        return Array.from(selection).map((k) => {
            const [c, y] = k.split('::');
            return { company_id: parseInt(c, 10), fiscal_year: parseInt(y, 10) };
        });
    }

    async function refresh(companyId: number, year: number) {
        const k = `${companyId}::${year}::refresh`;
        setBusyKey(k);
        setLastOutput(null);
        setError('');
        try {
            const r = await mutate<{ ok: boolean; output: string }>('/admin/period-locks/refresh-snapshot', token, 'POST', { company_id: companyId, fiscal_year: year });
            setLastOutput({ ok: r.ok, output: r.output });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBusyKey(null);
        }
    }

    async function lock(companyId: number, year: number) {
        const lock = lockMap.get(cellKey(companyId, year));
        if (!lock?.last_snapshot_at) {
            if (!confirm(t('Snapshot za ovu godinu još ne postoji. Pokrenuti Refresh prvo, pa zaključati?', 'Snapshot does not exist yet. Run Refresh first and then lock?'))) {
                return;
            }
            const k0 = `${companyId}::${year}::refresh`;
            setBusyKey(k0);
            try {
                await mutate('/admin/period-locks/refresh-snapshot', token, 'POST', { company_id: companyId, fiscal_year: year });
                await load();
            } catch (e) {
                setError(e instanceof Error ? e.message : 'API');
                setBusyKey(null);
                return;
            } finally {
                setBusyKey(null);
            }
        }
        const reason = window.prompt(t('Razlog zaključavanja (npr. revizija završena):', 'Lock reason (e.g. audit complete):'), '') ?? '';
        const k = `${companyId}::${year}::lock`;
        setBusyKey(k);
        setError('');
        try {
            await mutate('/admin/period-locks/lock', token, 'POST', { company_id: companyId, fiscal_year: year, reason });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBusyKey(null);
        }
    }

    async function unlock(companyId: number, year: number) {
        if (! confirm(t('Otključati godinu? Read switch ide nazad na live query dok ne lockaš ponovo.', 'Unlock the year? Read switch goes back to live query until you re-lock.'))) return;
        const reason = window.prompt(t('Razlog otključavanja:', 'Unlock reason:'), '') ?? '';
        const k = `${companyId}::${year}::unlock`;
        setBusyKey(k);
        setError('');
        try {
            await mutate('/admin/period-locks/unlock', token, 'POST', { company_id: companyId, fiscal_year: year, reason });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBusyKey(null);
        }
    }

    async function batchRefresh() {
        const items = selectionItems();
        if (items.length === 0) return;
        if (!confirm(t(`Pokrenuti refresh snapshot za ${items.length} godina-firma kombinacija?`, `Run refresh snapshot for ${items.length} (company × year) cells?`))) return;
        setBatchBusy('refresh');
        setError('');
        setBatchSummary(null);
        try {
            const r = await mutate<BatchResponse>('/admin/period-locks/refresh-snapshot-batch', token, 'POST', { items });
            setBatchSummary({ kind: t('Refresh', 'Refresh'), ok: r.ok_count, failed: r.failed_count, failures: r.results.filter((x) => !x.ok) });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBatchBusy(null);
        }
    }

    async function batchLock() {
        const items = selectionItems();
        if (items.length === 0) return;
        const reason = window.prompt(t(`Razlog zaključavanja za ${items.length} kombinacija:`, `Lock reason for ${items.length} cells:`), '') ?? '';
        setBatchBusy('lock');
        setError('');
        setBatchSummary(null);
        try {
            const r = await mutate<BatchResponse>('/admin/period-locks/lock-batch', token, 'POST', { items, reason });
            setBatchSummary({ kind: t('Lock', 'Lock'), ok: r.ok_count, failed: r.failed_count, failures: r.results.filter((x) => !x.ok) });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBatchBusy(null);
        }
    }

    async function batchUnlock() {
        const items = selectionItems();
        if (items.length === 0) return;
        if (!confirm(t(`Otključati ${items.length} godina-firma kombinacija?`, `Unlock ${items.length} cells?`))) return;
        const reason = window.prompt(t('Razlog otključavanja:', 'Unlock reason:'), '') ?? '';
        setBatchBusy('unlock');
        setError('');
        setBatchSummary(null);
        try {
            const r = await mutate<BatchResponse>('/admin/period-locks/unlock-batch', token, 'POST', { items, reason });
            setBatchSummary({ kind: t('Unlock', 'Unlock'), ok: r.ok_count, failed: r.failed_count, failures: r.results.filter((x) => !x.ok) });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBatchBusy(null);
        }
    }

    const selectionCount = selection.size;
    const companyById = useMemo(() => {
        const m = new Map<number, Company>();
        (payload?.companies ?? []).forEach((c) => m.set(c.id, c));
        return m;
    }, [payload]);

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🔒</div>
                <div className="bi-module-hero-text">
                    <h1>{title}</h1>
                    <p className="muted">
                        {t(
                            'Zaključavanje fiskalnih godina. Tijek: Otključaj → uvezi/popravi podatke → Refresh snapshot → Zaključaj. Zaključana godina čita iz pred-agregirane tablice (brže ~10×).',
                            'Lock fiscal years. Flow: Unlock → import/fix data → Refresh snapshot → Lock. Locked years are served from the pre-aggregated snapshot table (~10× faster).',
                        )}
                    </p>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {/* Batch action bar */}
            {!loading && payload ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    padding: '10px 14px',
                    background: selectionCount > 0 ? 'linear-gradient(135deg, rgba(var(--ad-acc-rgb),0.15), rgba(107,79,28,0.1))' : 'rgba(0,0,0,0.03)',
                    border: '1px solid rgba(var(--ad-soft-rgb),0.3)',
                    borderRadius: 10,
                    marginBottom: 14,
                    transition: 'background 0.2s ease',
                }}>
                    <strong style={{ fontSize: '0.86rem', color: 'var(--ad-ink)' }}>
                        {selectionCount > 0
                            ? `${selectionCount} ${t('odabranih', 'selected')}`
                            : t('Odaberi ćelije za batch akcije', 'Select cells for batch actions')}
                    </strong>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button type="button" className="secondary compact" onClick={selectAllUnlocked} disabled={batchBusy !== null} style={{ fontSize: '0.76rem' }}>
                            {t('Sve otključane', 'All unlocked')}
                        </button>
                        <button type="button" className="secondary compact" onClick={selectAllLocked} disabled={batchBusy !== null} style={{ fontSize: '0.76rem' }}>
                            {t('Sve zaključane', 'All locked')}
                        </button>
                        <button type="button" className="secondary compact" onClick={clearSelection} disabled={batchBusy !== null || selectionCount === 0} style={{ fontSize: '0.76rem' }}>
                            {t('Očisti', 'Clear')}
                        </button>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className="secondary compact"
                            onClick={() => void batchRefresh()}
                            disabled={batchBusy !== null || selectionCount === 0}
                            style={{ fontSize: '0.78rem' }}
                            title={t('Generiraj/osvježi snapshot za sve odabrane', 'Generate/refresh snapshot for selected')}
                        >
                            {batchBusy === 'refresh' ? '⏳' : '🔄'} {t('Refresh odabrano', 'Refresh selected')}
                        </button>
                        <button
                            type="button"
                            className="compact"
                            onClick={() => void batchLock()}
                            disabled={batchBusy !== null || selectionCount === 0}
                            style={{ background: '#1f7a36', color: '#fff', fontSize: '0.78rem' }}
                        >
                            {batchBusy === 'lock' ? '⏳' : '🔒'} {t('Lock odabrano', 'Lock selected')}
                        </button>
                        <button
                            type="button"
                            className="secondary compact"
                            onClick={() => void batchUnlock()}
                            disabled={batchBusy !== null || selectionCount === 0}
                            style={{ color: '#b14040', fontSize: '0.78rem' }}
                        >
                            {batchBusy === 'unlock' ? '⏳' : '🔓'} {t('Unlock odabrano', 'Unlock selected')}
                        </button>
                    </div>
                </div>
            ) : null}

            {batchSummary ? (
                <div style={{
                    padding: '10px 14px',
                    marginBottom: '1rem',
                    borderRadius: 10,
                    background: batchSummary.failed === 0 ? 'rgba(31,122,54,0.08)' : 'rgba(var(--ad-acc-rgb),0.08)',
                    border: batchSummary.failed === 0 ? '1px solid rgba(31,122,54,0.3)' : '1px solid rgba(var(--ad-acc-rgb),0.4)',
                    fontSize: '0.85rem',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ color: batchSummary.failed === 0 ? '#1f7a36' : 'var(--ad-acc-3)' }}>
                            {batchSummary.failed === 0 ? '✓' : '⚠'} {batchSummary.kind}: {batchSummary.ok} ok, {batchSummary.failed} {t('neuspjelo', 'failed')}
                        </strong>
                        <button type="button" onClick={() => setBatchSummary(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer' }}>×</button>
                    </div>
                    {batchSummary.failures.length > 0 ? (
                        <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: '0.78rem' }}>
                            {batchSummary.failures.slice(0, 10).map((f, i) => {
                                const cmp = companyById.get(f.company_id);
                                return (
                                    <li key={i} style={{ color: '#b14040' }}>
                                        {cmp?.code ?? f.company_id} · {f.fiscal_year}: {f.error ?? f.output ?? '—'}
                                    </li>
                                );
                            })}
                            {batchSummary.failures.length > 10 ? <li className="muted">… +{batchSummary.failures.length - 10}</li> : null}
                        </ul>
                    ) : null}
                </div>
            ) : null}

            {lastOutput ? (
                <div style={{
                    padding: '0.7rem 0.9rem',
                    marginBottom: '1rem',
                    borderRadius: 10,
                    background: lastOutput.ok ? 'rgba(31,122,54,0.08)' : 'rgba(177,64,64,0.08)',
                    border: lastOutput.ok ? '1px solid rgba(31,122,54,0.25)' : '1px solid rgba(177,64,64,0.25)',
                    fontSize: '0.85rem',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ color: lastOutput.ok ? '#1f7a36' : '#b14040' }}>
                            {lastOutput.ok ? '✓' : '✗'} {t('Snapshot dovršen', 'Snapshot complete')}
                        </strong>
                        <button type="button" onClick={() => setLastOutput(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer' }}>×</button>
                    </div>
                    {lastOutput.output ? <pre style={{ margin: '6px 0 0', fontSize: '0.74rem', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{lastOutput.output.trim()}</pre> : null}
                </div>
            ) : null}

            {!loading && payload ? (
                <div className="table-wrap">
                    <table className="admin-user-table">
                        <thead>
                            <tr>
                                <th>{t('Tvrtka', 'Company')}</th>
                                {YEAR_OPTIONS.map((y) => (
                                    <th key={y} style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => selectColumn(y)} title={t(`Označi sve ${y}`, `Select all ${y}`)}>
                                        {y}
                                        <div style={{ fontSize: '0.62rem', fontWeight: 400, color: 'var(--ad-acc-3)' }}>{t('označi sve', 'select all')}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {payload.companies.map((c) => (
                                <tr key={c.id}>
                                    <td>
                                        <button
                                            type="button"
                                            onClick={() => selectRow(c.id)}
                                            title={t('Označi sve godine za ovu firmu', 'Select all years for this company')}
                                            style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}
                                        >
                                            <strong>{c.code}</strong>
                                            <span className="muted" style={{ marginLeft: 6 }}>{c.name}</span>
                                        </button>
                                    </td>
                                    {YEAR_OPTIONS.map((y) => {
                                        const row = lockMap.get(cellKey(c.id, y)) ?? null;
                                        const isLocked = !!row?.locked_at;
                                        const hasSnapshot = !!row?.last_snapshot_at;
                                        const isSelected = selection.has(cellKey(c.id, y));
                                        return (
                                            <td
                                                key={y}
                                                onClick={(e) => {
                                                    // Klik na ćeliju (bilo gdje osim na button) → toggle selection
                                                    if ((e.target as HTMLElement).closest('button')) return;
                                                    toggleCell(c.id, y);
                                                }}
                                                style={{
                                                    verticalAlign: 'top',
                                                    padding: '6px 8px',
                                                    borderLeft: '1px solid rgba(var(--ad-soft-rgb),0.15)',
                                                    background: isSelected ? 'rgba(var(--ad-acc-rgb),0.18)' : (isLocked ? 'rgba(31,122,54,0.04)' : undefined),
                                                    cursor: 'pointer',
                                                    transition: 'background 0.1s ease',
                                                }}
                                            >
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleCell(c.id, y)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ margin: 0, cursor: 'pointer', accentColor: 'var(--ad-acc)' }}
                                                        />
                                                        {isLocked ? (
                                                            <span style={{ background: 'rgba(31,122,54,0.13)', color: '#1f7a36', padding: '2px 6px', borderRadius: 4, fontWeight: 700, fontSize: '0.72rem' }}>
                                                                🔒 {t('Zaključano', 'Locked')}
                                                            </span>
                                                        ) : (
                                                            <span style={{ background: 'rgba(0,0,0,0.06)', color: '#666', padding: '2px 6px', borderRadius: 4, fontWeight: 600, fontSize: '0.72rem' }}>
                                                                ⚪ {t('Live', 'Live')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {hasSnapshot ? (
                                                        <span className="muted" style={{ fontSize: '0.7rem' }} title={row?.last_snapshot_at ?? ''}>
                                                            📸 {row?.last_snapshot_at ? formatDateTime(row.last_snapshot_at, locale) : '—'}
                                                        </span>
                                                    ) : (
                                                        <span className="muted" style={{ fontSize: '0.7rem' }}>📸 —</span>
                                                    )}
                                                    {isLocked && row?.locked_by_name ? (
                                                        <span className="muted" style={{ fontSize: '0.68rem' }} title={row?.lock_reason ?? ''}>👤 {row.locked_by_name}</span>
                                                    ) : null}
                                                    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                                                        <button
                                                            type="button"
                                                            className="secondary compact"
                                                            disabled={busyKey !== null || batchBusy !== null}
                                                            onClick={(e) => { e.stopPropagation(); void refresh(c.id, y); }}
                                                            title={t('Generiraj/osvježi snapshot', 'Generate/refresh snapshot')}
                                                            style={{ fontSize: '0.72rem', padding: '3px 7px' }}
                                                        >
                                                            {busyKey === `${c.id}::${y}::refresh` ? '⏳' : '🔄'} {t('Refresh', 'Refresh')}
                                                        </button>
                                                        {!isLocked ? (
                                                            <button
                                                                type="button"
                                                                className="compact"
                                                                disabled={busyKey !== null || batchBusy !== null}
                                                                onClick={(e) => { e.stopPropagation(); void lock(c.id, y); }}
                                                                style={{ background: '#1f7a36', color: '#fff', fontSize: '0.72rem', padding: '3px 7px' }}
                                                            >
                                                                {busyKey === `${c.id}::${y}::lock` ? '⏳' : '🔒'} {t('Lock', 'Lock')}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="secondary compact"
                                                                disabled={busyKey !== null || batchBusy !== null}
                                                                onClick={(e) => { e.stopPropagation(); void unlock(c.id, y); }}
                                                                style={{ color: '#b14040', fontSize: '0.72rem', padding: '3px 7px' }}
                                                            >
                                                                {busyKey === `${c.id}::${y}::unlock` ? '⏳' : '🔓'} {t('Unlock', 'Unlock')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p className="muted" style={{ fontSize: '0.76rem', marginTop: 8 }}>
                        💡 {t('Klikni na ćeliju za odabir. Klikni na zaglavlje godine za odabir cijele kolone. Klikni na šifru firme za odabir cijelog reda.', 'Click a cell to select. Click a year header to select the whole column. Click company code to select the whole row.')}
                    </p>
                </div>
            ) : null}
        </section>
    );
}
